import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { CheckCircle, ArrowLeft, Save, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function VCRStage4({ report, onPrevious, onSave, onClose }) {
  const [formData, setFormData] = useState({
    blameworthy_decision: '',
    investigation_summary: '',
    root_cause_analysis: '',
    corrective_actions: '',
    preventive_measures: '',
    manager_comments: ''
  });

  useEffect(() => {
    if (report.stage4_data) {
      setFormData(report.stage4_data);
    }
  }, [report]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const downloadSHEAlert = async () => {
    try {
      const response = await axios.get(
        `${API}/vcr/${report.id}/she-alert`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `SHE_Alert_${report.id}.docx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('SHE Alert downloaded - Complete and attach to accident');
    } catch (error) {
      toast.error('Failed to download SHE Alert');
    }
  };

  const downloadSHERCA = async () => {
    try {
      const response = await axios.get(
        `${API}/vcr/${report.id}/she-rca`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `SHE_RCA_${report.id}.docx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('SHE RCA downloaded - Complete and attach to accident');
    } catch (error) {
      toast.error('Failed to download SHE RCA');
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/vcr/${report.id}/stage4`, formData, getAuthHeaders());
      toast.success('Progress saved');
      if (onSave) onSave();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleComplete = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(
        `${API}/vcr/${report.id}/complete`,
        formData,
        getAuthHeaders()
      );
      
      // Generate final completion email
      const stage1 = report.stage1_data || {};
      const emailSubject = `VCR COMPLETED - ${stage1.employee_name} - ${new Date().toLocaleString()}`;
      const emailBody = `Vehicle Collision Report - FINAL COMPLETION

VCR ID: ${report.id}
Employee: ${stage1.employee_name}
Incident: ${stage1.incident_date} ${stage1.incident_time}

INVESTIGATION COMPLETE:

Summary:
${formData.investigation_summary}

Root Cause:
${formData.root_cause_analysis}

Corrective Actions:
${formData.corrective_actions}

Preventive Measures:
${formData.preventive_measures}

Manager Comments:
${formData.manager_comments}

---
All 4 stages completed. Accident record automatically created for driver.
Full VCR available in system.`;

      const recipients = 'david.l.mills@royalmail.com,richard.ohalloran@royalmail.com,simon.ashley@royalmail.com,chris.s.palmer@royalmail.com,adam.moat@royalmail.com,james.sangwine@royalmail.com,john.b.clark1@royalmail.com,david.spittle@royalmail.com,kola.akindiya@royalmail.com,balaji.jeyaprakasam@royalmail.com,andrew.r.gardner@royalmail.com';
      const mailtoLink = `mailto:${recipients}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
      window.location.href = mailtoLink;
      
      toast.success('VCR completed! Accident record created and email opened.');
      setTimeout(() => {
        if (onClose) onClose();
      }, 1000);
    } catch (error) {
      toast.error('Failed to complete VCR');
    }
  };

  return (
    <form onSubmit={handleComplete} className="space-y-6">
      <Card className="border-green-300 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="text-green-600">Stage 4: Investigation & Completion</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Blameworthy Decision *</Label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                className={`p-4 rounded-lg border-2 transition-all ${
                  formData.blameworthy_decision === 'non-blameworthy'
                    ? 'border-green-500 bg-green-50'
                    : 'border-slate-300 hover:border-green-300'
                }`}
                onClick={() => handleChange('blameworthy_decision', 'non-blameworthy')}
              >
                <p className="font-bold text-green-700">Non-Blameworthy</p>
                <p className="text-xs text-slate-600 dark:text-slate-400">Driver not at fault</p>
              </button>
              <button
                type="button"
                className={`p-4 rounded-lg border-2 transition-all ${
                  formData.blameworthy_decision === 'blameworthy'
                    ? 'border-red-500 bg-red-50'
                    : 'border-slate-300 hover:border-red-300'
                }`}
                onClick={() => handleChange('blameworthy_decision', 'blameworthy')}
              >
                <p className="font-bold text-red-700">Blameworthy</p>
                <p className="text-xs text-slate-600 dark:text-slate-400">Driver at fault</p>
              </button>
            </div>
            {!formData.blameworthy_decision && (
              <p className="text-xs text-red-600">Please select a blameworthy decision</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Investigation Summary *</Label>
            <Textarea
              value={formData.investigation_summary}
              onChange={(e) => handleChange('investigation_summary', e.target.value)}
              rows={4}
              placeholder="Comprehensive summary of the investigation..."
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Root Cause Analysis *</Label>
            <Textarea
              value={formData.root_cause_analysis}
              onChange={(e) => handleChange('root_cause_analysis', e.target.value)}
              rows={4}
              placeholder="Analysis of underlying causes..."
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Corrective Actions Taken</Label>
            <Textarea
              value={formData.corrective_actions}
              onChange={(e) => handleChange('corrective_actions', e.target.value)}
              rows={3}
              placeholder="Actions taken to address this incident..."
            />
          </div>

          <div className="space-y-2">
            <Label>Preventive Measures</Label>
            <Textarea
              value={formData.preventive_measures}
              onChange={(e) => handleChange('preventive_measures', e.target.value)}
              rows={3}
              placeholder="Measures to prevent future occurrences..."
            />
          </div>

          <div className="space-y-2">
            <Label>Manager Comments</Label>
            <Textarea
              value={formData.manager_comments}
              onChange={(e) => handleChange('manager_comments', e.target.value)}
              rows={3}
              placeholder="Final manager comments and recommendations..."
            />
          </div>
        </CardContent>
      </Card>

      {/* SHE Forms Section */}
      <Card className="border-blue-300 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="text-blue-700">SHE Documentation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Download pre-filled SHE forms, complete in Word, and attach to the accident record
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={downloadSHEAlert}
              className="flex items-center justify-center"
            >
              <Download className="h-4 w-4 mr-2" />
              Download SHE Alert
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={downloadSHERCA}
              className="flex items-center justify-center"
            >
              <FileText className="h-4 w-4 mr-2" />
              Download SHE RCA
            </Button>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Forms auto-filled with VCR data. Complete remaining fields in Word, then attach to driver's accident record.
          </p>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onPrevious}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Stage 3
        </Button>
        <div className="flex space-x-2">
          <Button type="button" variant="outline" onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            Save
          </Button>
          <Button type="submit" className="bg-green-600 hover:bg-green-700">
            <CheckCircle className="h-4 w-4 mr-2" />
            Complete VCR
          </Button>
        </div>
      </div>
    </form>
  );
}
